import { observer } from "mobx-react-lite";

import { Heading } from "@bps/fluent-ui";
import {
  OutboundCommChannel,
  OutboundCommType
} from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useBookingCalendarScreenContext } from "@modules/booking/screens/booking-calendar/context/BookingCalendarScreenContext.tsx";
import { CalendarEventReminderSearchModel } from "@stores/booking/models/CalendarEventReminderSearchModel.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

interface AddMobileModalProps {
  item: CalendarEventReminderSearchModel;
  onDismiss: () => void;
  onSaved: () => void;
}

interface AddMobileFormValues {
  mobile: string;
  resendReminder: boolean;
}

const nameOf = nameOfFactory<AddMobileFormValues>();

export const AddMobileModal: React.FC<AddMobileModalProps> = observer(
  ({ item, onDismiss, onSaved }) => {
    const { practice, comms, notification } = useStores();
    const { setReminderArgs } = useBookingCalendarScreenContext();

    const onSubmit = async (values: AddMobileFormValues) => {
      await practice.updatePatient({
        id: item.patient!.id,
        communications: [
          ...(item.patient?.communications ?? []).filter(
            c => c.type !== CommunicationType.Mobile
          ),
          {
            type: CommunicationType.Mobile,
            value: values.mobile,
            preferred: true
          }
        ]
      });

      const contactPreferences = item.patient?.contactPreferences;
      const existingPrefs = contactPreferences?.commTypePreferences ?? [];
      const existingApptRem = existingPrefs.find(
        p => p.commTypeCode === OutboundCommType.ApptReminder
      );

      const commTypePreferences = [
        ...existingPrefs.filter(p => p !== existingApptRem),
        {
          ...existingApptRem,
          commTypeCode: OutboundCommType.ApptReminder,
          preferredCommChannelTypeCode: OutboundCommChannel.Sms,
          preferredCommAddressValue: values.mobile,
          contactHasOptedOut: false
        }
      ];

      if (contactPreferences) {
        await comms.patchContactPreferences({
          id: contactPreferences.id,
          eTag: contactPreferences.eTag,
          commTypePreferences
        });
      } else {
        await comms.addContactPreferences({
          id: item.patientId,
          commTypePreferences
        });
      }

      if (values.resendReminder) {
        setReminderArgs({
          calendarEventId: item.calendarEvent.id,
          contactId: item.patientId
        });
      }
    };

    const onSubmitSucceeded = () => {
      onSaved();
      onDismiss();
    };

    return (
      <SubmissionFormDialog<AddMobileFormValues>
        dialogName={`Add mobile for ${item.patientName}`}
        onSubmit={onSubmit}
        onSubmitSucceeded={onSubmitSucceeded}
        onSubmitFailed={() =>
          notification.error("Failed to save mobile number. Please try again.")
        }
        initialValues={{ mobile: "", resendReminder: false }}
        buttonsProps={{
          submitButtonProps: { text: "Save" },
          cancelButtonProps: { text: "Cancel" },
          hideButtonsSeparator: true
        }}
        dialogProps={{
          minWidth: 500,
          onDismiss,
          dialogContentProps: {
            title: `Add mobile for ${item.patientName}`
          }
        }}
      >
        {() => (
          <>
            <TextInputField
              name={nameOf("mobile")}
              label="Add mobile"
              required
              styles={{ fieldGroup: { width: 220 } }}
            />
            <Heading labelPaddings>Appointment reminder</Heading>
            <CheckboxField
              name={nameOf("resendReminder")}
              label="Resend reminder SMS"
            />
          </>
        )}
      </SubmissionFormDialog>
    );
  }
);
